import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useMe, useUsers } from "@/api/queries";
import { useTheme } from "@/app/theme-context";
import { currentConfig } from "@/auth/config";
import { clearSession, devUser, setCurrentTenant, setDevUser, signOut } from "@/auth/session";
import { useSession } from "@/api/onboarding";
import { Glyph } from "@/components/ui/Glyph";
import { LogoTile } from "@/components/ui/Logo";
import type { GlyphName } from "@/lib/glyphs";
import { PulseLine } from "./PulseLine";
import { useShell } from "./shell-context";
import { PHONE, useMediaQuery } from "./useMediaQuery";
import styles from "./shell.module.css";

export interface PrimaryAction {
  label: string;
  to: string;
  glyph?: GlyphName;
}

const DEFAULT_PRIMARY: PrimaryAction = {
  label: "Describe a fix",
  to: "/flows/new",
  glyph: "sparkle",
};

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  approver: "Approver",
  operator: "Operator",
  viewer: "Viewer",
};

const ITEM = '[role="menuitem"], [role="menuitemradio"]';

function AccountMenu() {
  const me = useMe();
  const qc = useQueryClient();
  const shell = useShell();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const dev = currentConfig().authMode === "dev";
  const users = useUsers();
  const session = useSession();

  useEffect(() => {
    if (!open) return;
    // The first item takes focus, so the arrow keys work straight away.
    menu.current?.querySelector<HTMLElement>(ITEM)?.focus();
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const shut = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = [...(menu.current?.querySelectorAll<HTMLElement>(ITEM) ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === "ArrowDown") next = (at + 1) % items.length;
    else if (e.key === "ArrowUp") next = (at - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      shut();
      return;
    } else if (e.key === "Tab") {
      shut(false);
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      items[next]?.focus();
    }
  };

  const user = me.data;
  const viewAs = (email: string) => {
    clearSession();
    setDevUser(email);
    shut();
    qc.clear();
    void qc.invalidateQueries();
  };
  const actingAs = devUser() ?? user?.email ?? currentConfig().devUser;
  const tenants = session.data?.tenants ?? [];
  const here = user?.tenant.slug;

  return (
    <div ref={ref} className={styles.account}>
      <button
        ref={button}
        type="button"
        className={`${styles.avatar} zh-press`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          // Starts with the initials shown on the button, so the spoken name matches what's seen.
          user
            ? `${user.initials}, account: ${user.name}, ${ROLE_LABEL[user.role] ?? user.role}`
            : "Account"
        }
        onClick={() => setOpen((o) => !o)}
      >
        {user?.initials ?? ""}
      </button>
      {open && user ? (
        <div
          ref={menu}
          role="menu"
          aria-label="Account"
          className={`zh-glass ${styles.menu}`}
          onKeyDown={onMenuKey}
        >
          <div className={styles.menuWho}>
            <div className={styles.menuName}>{user.name}</div>
            <div className={styles.menuSub}>
              {[ROLE_LABEL[user.role], user.team].filter(Boolean).join(", ")}
            </div>
            {user.approval_scope_label ? (
              <div className={styles.menuSub}>Approves {user.approval_scope_label}</div>
            ) : null}
          </div>
          <Link
            role="menuitem"
            className={styles.menuItem}
            to="/settings"
            onClick={() => shut(false)}
          >
            Settings
          </Link>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              shut(false);
              shell.openShortcuts();
            }}
          >
            Keyboard shortcuts
            <kbd className={styles.menuKey}>?</kbd>
          </button>
          {tenants.length > 1 ? (
            <>
              <div className={styles.menuLabel} role="presentation">
                Workspaces
              </div>
              {tenants.map((t) => (
                <button
                  key={t.slug}
                  type="button"
                  role="menuitemradio"
                  aria-checked={t.slug === here}
                  className={styles.menuItem}
                  onClick={() => {
                    shut(false);
                    if (t.slug === here) return;
                    setCurrentTenant(t.slug);
                    window.location.assign("/");
                  }}
                >
                  <span>{t.name}</span>
                  <span className={styles.menuRole}>{t.role ? ROLE_LABEL[t.role] : ""}</span>
                </button>
              ))}
            </>
          ) : null}
          {session.data?.is_superadmin ? (
            <Link
              role="menuitem"
              className={styles.menuItem}
              to="/platform"
              onClick={() => shut(false)}
            >
              Platform console
            </Link>
          ) : null}
          {dev ? (
            <>
              <div className={styles.menuLabel} role="presentation">
                View as (development only)
              </div>
              {(users.data ?? []).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={u.email === actingAs}
                  className={styles.menuItem}
                  onClick={() => viewAs(u.email)}
                >
                  <span>{u.name}</span>
                  <span className={styles.menuRole}>{ROLE_LABEL[u.role]}</span>
                </button>
              ))}
            </>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Page header: title, environment, the screen's main action, search, crew status, theme, alerts
 * and account. On a phone it keeps the logo, the title, the action, search and account; the
 * rest lives in the tab bar and its More sheet.
 */
export function Topbar({
  heading,
  primary = DEFAULT_PRIMARY,
}: {
  heading: string;
  primary?: PrimaryAction | null;
}) {
  const me = useMe();
  const { theme, toggle } = useTheme();
  const { openPalette } = useShell();
  const phone = useMediaQuery(PHONE);

  const user = me.data;
  const waiting = user?.needs_you_count ?? 0;

  if (phone) {
    return (
      <header className={`${styles.topbar} ${styles.topbarPhone}`}>
        <Link to="/" aria-label="ZeaOps home" className={styles.phoneLogo}>
          <LogoTile size={32} />
        </Link>
        <h1 className={styles.heading}>{heading}</h1>
        <span className={styles.grow} />
        {primary ? (
          <Link
            to={primary.to}
            className={
              (primary.glyph ?? "sparkle") === "sparkle"
                ? `${styles.roundAi} zh-press zh-ai-btn`
                : `${styles.round} ${styles.primaryRound} zh-press`
            }
            aria-label={primary.label}
            title={primary.label}
          >
            <Glyph name={primary.glyph ?? "sparkle"} size={16} color="currentColor" />
          </Link>
        ) : null}
        <button
          type="button"
          className={`${styles.round} zh-press`}
          aria-label="Search"
          onClick={() => openPalette()}
        >
          <Glyph name="search" size={18} strokeWidth={2} />
        </button>
        <AccountMenu />
        <PulseLine />
      </header>
    );
  }

  return (
    <header className={styles.topbar}>
      <h1 className={styles.heading}>{heading}</h1>
      {user ? (
        <span className={styles.env} title="Workspace and environment">
          <span className={styles.envDot} />
          {user.tenant.name}, {user.tenant.environment_label}
        </span>
      ) : null}
      <span className={styles.grow} />
      {primary ? (
        <Link
          to={primary.to}
          className={`${styles.primary} zh-press ${(primary.glyph ?? "sparkle") === "sparkle" ? "zh-ai-btn" : styles.primaryPlain}`}
        >
          <Glyph name={primary.glyph ?? "sparkle"} size={14} color="currentColor" />
          {primary.label}
        </Link>
      ) : null}
      <button
        type="button"
        className={styles.search}
        onClick={() => openPalette()}
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Glyph name="search" size={15} />
        <span>
          Search<span className="visually-hidden"> cases and flows</span>
        </span>
        <span className={`${styles.kbd} ${styles.hideMd}`} aria-hidden="true">
          Ctrl K
        </span>
      </button>
      {user ? (
        <Link to="/crew" className={`${styles.agentsLive} ${styles.hideMd}`}>
          <span className={`zh-dot ${styles.liveDot}`} />
          {user.agents_active} of {user.agents_total} agents active
        </Link>
      ) : null}
      <button
        type="button"
        className={`${styles.round} zh-press`}
        aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
        onClick={toggle}
      >
        <Glyph name={theme === "light" ? "moon" : "sun"} size={17} strokeWidth={1.8} />
      </button>
      <Link
        to="/needs-you"
        className={`${styles.round} zh-press`}
        aria-label={waiting ? `Needs you, ${waiting} waiting` : "Needs you, nothing waiting"}
      >
        <Glyph name="bell" size={17} strokeWidth={1.8} />
        {waiting ? <span className={styles.dotAlert} /> : null}
      </Link>
      <AccountMenu />
      <PulseLine />
    </header>
  );
}
